/**
 * AI Models Service
 * Manages AI providers and models for admin panel
 * Syncs to IDE via JSON endpoint
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Types
export type AIProvider = 'openai' | 'anthropic' | 'google' | 'xai' | 'ollama' | 'mistral' | 'cohere';

export interface AIModel {
  id: string;
  name: string;
  displayName: string;
  provider: AIProvider;
  description: string;
  contextLength: number;
  maxTokens: number;
  inputPrice: number;  // per 1M tokens
  outputPrice: number; // per 1M tokens
  capabilities: ('chat' | 'code' | 'vision' | 'function_calling' | 'json_mode')[];
  enabled: boolean;
  isDefault: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProviderConfig {
  id: AIProvider;
  name: string;
  enabled: boolean;
  apiKeyConfigured: boolean;
  baseUrl?: string;
  icon: string;
  color: string;
  models: AIModel[];
}

// Provider metadata
export const PROVIDER_METADATA: Record<AIProvider, { name: string; icon: string; color: string }> = {
  openai: { name: 'OpenAI', icon: 'Bot', color: 'green' },
  anthropic: { name: 'Anthropic', icon: 'Brain', color: 'orange' },
  google: { name: 'Google AI', icon: 'Sparkles', color: 'blue' },
  xai: { name: 'xAI (Grok)', icon: 'Zap', color: 'purple' },
  ollama: { name: 'Ollama', icon: 'Server', color: 'gray' },
  mistral: { name: 'Mistral AI', icon: 'Wind', color: 'cyan' },
  cohere: { name: 'Cohere', icon: 'Layers', color: 'pink' },
};

// Default models
const DEFAULT_MODELS: AIModel[] = [
  // OpenAI Models
  {
    id: 'gpt-4-turbo',
    name: 'gpt-4-turbo',
    displayName: 'GPT-4 Turbo',
    provider: 'openai',
    description: 'Most capable GPT-4 model with vision capabilities',
    contextLength: 128000,
    maxTokens: 4096,
    inputPrice: 10,
    outputPrice: 30,
    capabilities: ['chat', 'code', 'vision', 'function_calling', 'json_mode'],
    enabled: true,
    isDefault: true,
    order: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'gpt-4o',
    name: 'gpt-4o',
    displayName: 'GPT-4o',
    provider: 'openai',
    description: 'Latest multimodal model with improved capabilities',
    contextLength: 128000,
    maxTokens: 4096,
    inputPrice: 5,
    outputPrice: 15,
    capabilities: ['chat', 'code', 'vision', 'function_calling', 'json_mode'],
    enabled: true,
    isDefault: false,
    order: 2,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'gpt-4o-mini',
    name: 'gpt-4o-mini',
    displayName: 'GPT-4o Mini',
    provider: 'openai',
    description: 'Fast and affordable small model',
    contextLength: 128000,
    maxTokens: 16384,
    inputPrice: 0.15,
    outputPrice: 0.6,
    capabilities: ['chat', 'code', 'vision', 'function_calling', 'json_mode'],
    enabled: true,
    isDefault: false,
    order: 3,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'gpt-3.5-turbo',
    name: 'gpt-3.5-turbo',
    displayName: 'GPT-3.5 Turbo',
    provider: 'openai',
    description: 'Fast and cost-effective model',
    contextLength: 16385,
    maxTokens: 4096,
    inputPrice: 0.5,
    outputPrice: 1.5,
    capabilities: ['chat', 'code', 'function_calling', 'json_mode'],
    enabled: true,
    isDefault: false,
    order: 4,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  // Anthropic Models
  {
    id: 'claude-sonnet-4-20250514',
    name: 'claude-sonnet-4-20250514',
    displayName: 'Claude Sonnet 4',
    provider: 'anthropic',
    description: 'Latest and most capable Claude model',
    contextLength: 200000,
    maxTokens: 8192,
    inputPrice: 3,
    outputPrice: 15,
    capabilities: ['chat', 'code', 'vision', 'function_calling'],
    enabled: true,
    isDefault: true,
    order: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'claude-3-5-sonnet-20241022',
    name: 'claude-3-5-sonnet-20241022',
    displayName: 'Claude 3.5 Sonnet',
    provider: 'anthropic',
    description: 'Fast and intelligent model',
    contextLength: 200000,
    maxTokens: 8192,
    inputPrice: 3,
    outputPrice: 15,
    capabilities: ['chat', 'code', 'vision', 'function_calling'],
    enabled: true,
    isDefault: false,
    order: 2,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'claude-3-opus-20240229',
    name: 'claude-3-opus-20240229',
    displayName: 'Claude 3 Opus',
    provider: 'anthropic',
    description: 'Most capable for complex tasks',
    contextLength: 200000,
    maxTokens: 4096,
    inputPrice: 15,
    outputPrice: 75,
    capabilities: ['chat', 'code', 'vision', 'function_calling'],
    enabled: true,
    isDefault: false,
    order: 3,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'claude-3-haiku-20240307',
    name: 'claude-3-haiku-20240307',
    displayName: 'Claude 3 Haiku',
    provider: 'anthropic',
    description: 'Fastest and most compact',
    contextLength: 200000,
    maxTokens: 4096,
    inputPrice: 0.25,
    outputPrice: 1.25,
    capabilities: ['chat', 'code', 'vision', 'function_calling'],
    enabled: true,
    isDefault: false,
    order: 4,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  // Google Models
  {
    id: 'gemini-1.5-pro',
    name: 'gemini-1.5-pro',
    displayName: 'Gemini 1.5 Pro',
    provider: 'google',
    description: 'Advanced reasoning with long context',
    contextLength: 1000000,
    maxTokens: 8192,
    inputPrice: 3.5,
    outputPrice: 10.5,
    capabilities: ['chat', 'code', 'vision', 'function_calling', 'json_mode'],
    enabled: true,
    isDefault: true,
    order: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'gemini-1.5-flash',
    name: 'gemini-1.5-flash',
    displayName: 'Gemini 1.5 Flash',
    provider: 'google',
    description: 'Fast and versatile multimodal model',
    contextLength: 1000000,
    maxTokens: 8192,
    inputPrice: 0.075,
    outputPrice: 0.3,
    capabilities: ['chat', 'code', 'vision', 'function_calling', 'json_mode'],
    enabled: true,
    isDefault: false,
    order: 2,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  // xAI Models
  {
    id: 'grok-2',
    name: 'grok-2',
    displayName: 'Grok 2',
    provider: 'xai',
    description: 'xAI flagship model with real-time knowledge',
    contextLength: 131072,
    maxTokens: 4096,
    inputPrice: 2,
    outputPrice: 10,
    capabilities: ['chat', 'code', 'function_calling'],
    enabled: true,
    isDefault: true,
    order: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  // Mistral Models
  {
    id: 'mistral-large',
    name: 'mistral-large-latest',
    displayName: 'Mistral Large',
    provider: 'mistral',
    description: 'Flagship model for complex tasks',
    contextLength: 128000,
    maxTokens: 8192,
    inputPrice: 3,
    outputPrice: 9,
    capabilities: ['chat', 'code', 'function_calling', 'json_mode'],
    enabled: true,
    isDefault: true,
    order: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'codestral',
    name: 'codestral-latest',
    displayName: 'Codestral',
    provider: 'mistral',
    description: 'Specialized for code generation',
    contextLength: 32000,
    maxTokens: 8192,
    inputPrice: 1,
    outputPrice: 3,
    capabilities: ['code', 'function_calling'],
    enabled: true,
    isDefault: false,
    order: 2,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  // Ollama Models (Local)
  {
    id: 'llama3.2',
    name: 'llama3.2',
    displayName: 'Llama 3.2',
    provider: 'ollama',
    description: 'Latest Llama model for local inference',
    contextLength: 128000,
    maxTokens: 4096,
    inputPrice: 0,
    outputPrice: 0,
    capabilities: ['chat', 'code'],
    enabled: true,
    isDefault: true,
    order: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'codellama',
    name: 'codellama',
    displayName: 'Code Llama',
    provider: 'ollama',
    description: 'Specialized for code generation',
    contextLength: 16384,
    maxTokens: 4096,
    inputPrice: 0,
    outputPrice: 0,
    capabilities: ['code'],
    enabled: true,
    isDefault: false,
    order: 2,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'mistral',
    name: 'mistral',
    displayName: 'Mistral 7B',
    provider: 'ollama',
    description: 'Efficient open-source model',
    contextLength: 32768,
    maxTokens: 4096,
    inputPrice: 0,
    outputPrice: 0,
    capabilities: ['chat', 'code'],
    enabled: true,
    isDefault: false,
    order: 3,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

// Store interface
interface AIModelsState {
  models: AIModel[];
  enabledProviders: AIProvider[];
  isLoading: boolean;

  // Actions
  initializeModels: () => void;
  addModel: (model: Omit<AIModel, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateModel: (id: string, updates: Partial<AIModel>) => void;
  deleteModel: (id: string) => void;
  toggleModel: (id: string) => void;
  setDefaultModel: (provider: AIProvider, modelId: string) => void;
  toggleProvider: (provider: AIProvider) => void;
  getModelsByProvider: (provider: AIProvider) => AIModel[];
  getEnabledModels: () => AIModel[];
  getProviderConfigs: () => ProviderConfig[];
  exportToJSON: () => string;
}

// Storage key
const STORAGE_KEY = 'ochtopus-ai-models-admin';

// Zustand store
export const useAIModelsStore = create<AIModelsState>()(
  persist(
    (set, get) => ({
      models: DEFAULT_MODELS,
      enabledProviders: ['openai', 'anthropic', 'google', 'xai', 'ollama', 'mistral'],
      isLoading: false,

      initializeModels: () => {
        const { models } = get();
        if (models.length === 0) {
          set({ models: DEFAULT_MODELS });
        }
      },

      addModel: (modelData) => {
        const newModel: AIModel = {
          ...modelData,
          id: `model-${Date.now()}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        set((state) => ({
          models: [...state.models, newModel],
        }));
      },

      updateModel: (id, updates) => {
        set((state) => ({
          models: state.models.map((m) =>
            m.id === id ? { ...m, ...updates, updatedAt: new Date().toISOString() } : m
          ),
        }));
      },

      deleteModel: (id) => {
        set((state) => ({
          models: state.models.filter((m) => m.id !== id),
        }));
      },

      toggleModel: (id) => {
        set((state) => ({
          models: state.models.map((m) =>
            m.id === id ? { ...m, enabled: !m.enabled, updatedAt: new Date().toISOString() } : m
          ),
        }));
      },

      setDefaultModel: (provider, modelId) => {
        set((state) => ({
          models: state.models.map((m) =>
            m.provider === provider
              ? { ...m, isDefault: m.id === modelId, updatedAt: new Date().toISOString() }
              : m
          ),
        }));
      },

      toggleProvider: (provider) => {
        set((state) => ({
          enabledProviders: state.enabledProviders.includes(provider)
            ? state.enabledProviders.filter((p) => p !== provider)
            : [...state.enabledProviders, provider],
        }));
      },

      getModelsByProvider: (provider) => {
        return get().models.filter((m) => m.provider === provider);
      },

      getEnabledModels: () => {
        const { models, enabledProviders } = get();
        return models.filter((m) => m.enabled && enabledProviders.includes(m.provider));
      },

      getProviderConfigs: () => {
        const { models, enabledProviders } = get();
        return Object.entries(PROVIDER_METADATA).map(([id, meta]) => ({
          id: id as AIProvider,
          name: meta.name,
          enabled: enabledProviders.includes(id as AIProvider),
          apiKeyConfigured: false,
          icon: meta.icon,
          color: meta.color,
          models: models.filter((m) => m.provider === id),
        }));
      },

      exportToJSON: () => {
        const { models, enabledProviders } = get();
        const enabledModels = models.filter(
          (m) => m.enabled && enabledProviders.includes(m.provider)
        );

        const response = {
          success: true,
          data: {
            providers: Object.entries(PROVIDER_METADATA)
              .filter(([id]) => enabledProviders.includes(id as AIProvider))
              .map(([id, meta]) => ({
                id,
                name: meta.name,
                icon: meta.icon,
                color: meta.color,
                enabled: true,
                models: enabledModels
                  .filter((m) => m.provider === id)
                  .sort((a, b) => a.order - b.order)
                  .map((m) => ({
                    id: m.id,
                    name: m.name,
                    displayName: m.displayName,
                    description: m.description,
                    contextLength: m.contextLength,
                    maxTokens: m.maxTokens,
                    inputPrice: m.inputPrice,
                    outputPrice: m.outputPrice,
                    capabilities: m.capabilities,
                    isDefault: m.isDefault,
                  })),
              })),
          },
          timestamp: new Date().toISOString(),
          version: '1.0.0',
        };

        return JSON.stringify(response, null, 2);
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({
        models: state.models,
        enabledProviders: state.enabledProviders,
      }),
    }
  )
);

// Initialize on load
if (typeof window !== 'undefined') {
  useAIModelsStore.getState().initializeModels();
}
